"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { SlideMedia, hasSlideMedia, type SlideMediaFields } from "../slide-media";

interface Slide extends SlideMediaFields {
  title: string;
  subtitle?: string;
  description?: string;
  linkUrl?: string;
  buttonText?: string;
}

interface EditorialSplitHeroProps {
  slides: Slide[];
}

const ROTATE_MS = 4200;

// Adapted from Skiper 52 HoverExpand_001 for storefront slider data.
export function EditorialSplitHero({ slides }: EditorialSplitHeroProps) {
  const reduceMotion = useReducedMotion();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const visibleSlides = useMemo(() => slides.filter(hasSlideMedia), [slides]);

  useEffect(() => {
    if (reduceMotion || paused || visibleSlides.length <= 1) return;
    const timer = window.setInterval(() => {
      setActive((current) => (current + 1) % visibleSlides.length);
    }, ROTATE_MS);
    return () => window.clearInterval(timer);
  }, [paused, reduceMotion, visibleSlides.length]);

  if (visibleSlides.length === 0) return null;

  const activeSlide = visibleSlides[active] ?? visibleSlides[0];

  return (
    <section
      className="relative overflow-hidden rounded-[var(--sf-radius)] bg-[#f5f1ea] px-4 py-5 shadow-[0_28px_80px_rgba(72,55,31,0.12)] sm:px-5 sm:py-6"
      aria-roledescription="carousel"
      aria-label="Luxury vitrin slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_12%_15%,rgba(212,175,55,0.24),transparent_28%),radial-gradient(circle_at_88%_12%,rgba(15,23,42,0.08),transparent_24%)]" />

      <div className="relative mx-auto flex w-full max-w-[104rem] flex-col gap-5">
        <div className="flex items-end justify-between gap-6 px-1 sm:px-2">
          <div className="min-w-0">
            {activeSlide.subtitle && (
              <p
                className="mb-2 text-[11px] font-semibold uppercase tracking-[0.28em]"
                style={{ color: "var(--sf-hero-subtitle)" }}
              >
                {activeSlide.subtitle}
              </p>
            )}
            <h1
              className="max-w-3xl text-3xl leading-[1.02] text-pretty sm:text-5xl md:text-6xl"
              style={{ color: "var(--sf-hero-title)", fontFamily: "var(--sf-font-heading)" }}
            >
              {activeSlide.title}
            </h1>
          </div>
          <div className="hidden shrink-0 items-center gap-2 text-xs font-semibold text-[#5f4930] sm:flex">
            <span>{String(active + 1).padStart(2, "0")}</span>
            <span className="h-px w-12 bg-[#b8924a]" />
            <span>{String(visibleSlides.length).padStart(2, "0")}</span>
          </div>
        </div>

        <div className="flex w-full gap-2 overflow-x-auto pb-1 sm:gap-3 lg:overflow-hidden">
          {visibleSlides.map((slide, index) => {
            const isActive = active === index;

            return (
              <motion.div
                key={slide.id}
                role="button"
                tabIndex={0}
                initial={false}
                animate={{
                  flexGrow: isActive ? 1 : 0,
                  flexBasis: isActive ? "0%" : "clamp(6.25rem, 8.5vw, 8rem)",
                  minWidth: isActive ? "min(20rem, 78vw)" : "clamp(6.25rem, 8.5vw, 8rem)",
                }}
                transition={reduceMotion ? { duration: 0 } : { duration: 0.38, ease: [0.32, 0.72, 0, 1] }}
                onClick={() => setActive(index)}
                onFocus={() => setActive(index)}
                onHoverStart={() => setActive(index)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setActive(index);
                  }
                }}
                className="group relative h-[24rem] shrink-0 overflow-hidden rounded-3xl bg-[#e7dccb] text-left outline-none ring-offset-2 ring-offset-[#f5f1ea] transition-shadow focus-visible:ring-2 focus-visible:ring-[#b8924a] sm:h-[28rem] md:h-[32rem]"
                aria-label={`${index + 1}. görsel: ${slide.title}`}
                aria-current={isActive}
              >
                <SlideMedia
                  slide={slide}
                  priority={index === 0}
                  sizes={isActive ? "(max-width: 768px) 78vw, 58rem" : "8rem"}
                  className="object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent opacity-80" />
                <div className="absolute inset-y-0 left-0 w-px bg-white/30" />

                <AnimatePresence>
                  {isActive && (
                    <motion.div
                      initial={reduceMotion ? false : { opacity: 0, y: 18 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 10 }}
                      transition={{ duration: 0.24, ease: "easeOut" }}
                      className="absolute inset-x-0 bottom-0 flex min-w-[18rem] flex-col items-start gap-4 p-5 sm:p-7"
                    >
                      <div className="max-w-md">
                        {slide.description && (
                          <p className="mb-3 text-sm leading-relaxed text-white/82 sm:text-base">
                            {slide.description}
                          </p>
                        )}
                        {slide.linkUrl && (
                          <Link
                            href={slide.linkUrl}
                            className="inline-flex items-center rounded-full bg-white px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-[#171717] shadow-[0_12px_34px_rgba(0,0,0,0.22)] transition hover:-translate-y-0.5"
                            onClick={(event) => event.stopPropagation()}
                          >
                            {slide.buttonText || "Koleksiyonu Keşfet"}
                          </Link>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {!isActive && (
                  <span className="absolute left-1/2 top-1/2 origin-center -translate-x-1/2 -translate-y-1/2 rotate-[-90deg] whitespace-nowrap text-xs font-semibold uppercase tracking-[0.24em] text-white/85">
                    {slide.subtitle || String(index + 1).padStart(2, "0")}
                  </span>
                )}
              </motion.div>
            );
          })}
        </div>

        <div className="flex items-center px-1 sm:px-2">
          <div className="flex gap-2" aria-label="Slider göstergeleri">
            {visibleSlides.map((slide, index) => (
              <button
                key={slide.id}
                type="button"
                onClick={() => setActive(index)}
                className="h-1.5 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#b8924a]/50"
                style={{
                  width: active === index ? 34 : 10,
                  backgroundColor: active === index ? "var(--sf-accent)" : "rgba(95, 73, 48, 0.22)",
                }}
                aria-label={`${index + 1}. görsele git`}
                aria-current={active === index}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
